import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { ptBR } from 'date-fns/locale/pt-BR';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { toast } from "react-toastify";
import { apiService } from "../../../../services/api";
import { LancamentoFinanceiroFilters } from "../../../../types/financeiro";
import { generateExcel } from "../utils/generateExcel";
import { RelatorioFinanceiroDocument } from "./RelatorioFinanceiroDocument";

// Configuração Locale DatePicker
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];
const MONTHS_SHORT = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];
const customPtBR = {
  ...ptBR,
  localize: {
    ...ptBR.localize,
    month: (n: number) => MONTHS[n],
    monthShort: (n: number) => MONTHS_SHORT[n]
  }
};
registerLocale('pt-BR', customPtBR as any);

interface GerarRelatorioModalProps {
  show: boolean;
  onHide: () => void;
  defaultDataInicio: Date | null;
  defaultDataFim: Date | null;
}

const GerarRelatorioModal: React.FC<GerarRelatorioModalProps> = ({
  show,
  onHide,
  defaultDataInicio,
  defaultDataFim
}) => {
  const [dataInicio, setDataInicio] = useState<Date | null>(defaultDataInicio);
  const [dataFim, setDataFim] = useState<Date | null>(defaultDataFim);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);

  useEffect(() => {
    if (show) {
      setDataInicio(defaultDataInicio);
      setDataFim(defaultDataFim);
    }
  }, [show, defaultDataInicio, defaultDataFim]);

  const fetchDadosRelatorio = async () => {
    if (!dataInicio || !dataFim) {
      toast.warning("Selecione o período completo.");
      return null;
    }

    const filtros: LancamentoFinanceiroFilters = {
      data_inicio: dataInicio.toISOString().split('T')[0],
      data_fim: dataFim.toISOString().split('T')[0],
      resumo: false, // Queremos todos os detalhes
      ordenar_por: 'data_lancamento',
      ordem: 'ASC' // Cronológico
    };

    try {
      const response = await apiService.getLancamentosFinanceiros(filtros);
      
      // Buscar saldo para cálculos se necessário (embora o relatório use o saldo da transação)
      // A lógica do backend deve garantir que saldo_apos_transacao esteja correto.
      
      if (response.success && response.data) {
        let lancamentosData: any[] = [];
        if (Array.isArray(response.data)) {
          lancamentosData = response.data;
        } else {
          lancamentosData = (response.data as any).data || [];
        }
        return lancamentosData;
      }
      
      toast.error("Erro ao buscar dados para o relatório.");
      return null;
    } catch (error) {
      console.error(error);
      toast.error("Erro de conexão ao gerar relatório.");
      return null;
    }
  };

  const handleGerarPDF = async () => {
    setLoadingPDF(true);
    try {
      const dados = await fetchDadosRelatorio();
      if (!dados || dados.length === 0) {
        toast.info("Nenhum lançamento encontrado neste período.");
        setLoadingPDF(false);
        return;
      }

      const periodoStr = {
        inicio: dataInicio?.toLocaleDateString('pt-BR') || '',
        fim: dataFim?.toLocaleDateString('pt-BR') || ''
      };

      const blob = await pdf(
        <RelatorioFinanceiroDocument 
          lancamentos={dados} 
          periodo={periodoStr} 
        />
      ).toBlob();

      saveAs(blob, `Relatorio_Financeiro_${periodoStr.inicio.replace(/\//g, '-')}_${periodoStr.fim.replace(/\//g, '-')}.pdf`);
      toast.success("Relatório PDF gerado com sucesso!");
      onHide();

    } catch (err) {
      console.error("Erro PDF:", err);
      toast.error("Falha ao gerar o PDF.");
    } finally {
      setLoadingPDF(false);
    }
  };

  const handleGerarExcel = async () => {
    setLoadingExcel(true);
    try {
      const dados = await fetchDadosRelatorio();
      if (!dados || dados.length === 0) {
        toast.info("Nenhum lançamento encontrado neste período.");
        setLoadingExcel(false);
        return;
      }

      if (dataInicio && dataFim) {
         await generateExcel({
            lancamentos: dados,
            periodo: { inicio: dataInicio, fim: dataFim }
         });
         toast.success("Relatório Excel gerado com sucesso!");
         onHide();
      }

    } catch (err) {
      console.error("Erro Excel:", err);
      toast.error("Falha ao gerar o Excel.");
    } finally {
      setLoadingExcel(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="ti ti-file-analytics me-2"></i>
          Exportar Relatório Financeiro
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <p className="text-muted mb-3">Selecione o período que deseja incluir no relatório.</p>
          
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Data Início</label>
              <div className="icon-form">
                 <span className="form-icon"><i className="ti ti-calendar"></i></span>
                 <DatePicker
                    className="form-control"
                    selected={dataInicio}
                    onChange={(date) => setDataInicio(date)}
                    dateFormat="dd/MM/yyyy"
                    locale="pt-BR"
                    placeholderText="Início"
                 />
              </div>
            </div>
            <div className="col-md-6">
               <label className="form-label fw-semibold">Data Fim</label>
               <div className="icon-form">
                 <span className="form-icon"><i className="ti ti-calendar"></i></span>
                 <DatePicker
                    className="form-control"
                    selected={dataFim}
                    onChange={(date) => setDataFim(date)}
                    dateFormat="dd/MM/yyyy"
                    locale="pt-BR"
                    placeholderText="Fim"
                    minDate={dataInicio || undefined}
                 />
              </div>
            </div>
          </div>
        </div>

        <div className="alert alert-light border-primary mb-0">
          <div className="d-flex align-items-center mb-2">
            <i className="ti ti-info-circle text-primary me-2"></i>
            <h6 className="mb-0 text-primary">Informações do Relatório</h6>
          </div>
          <small className="text-muted d-block">
            O relatório incluirá todos os lançamentos (Receitas, Despesas e Transferências) confirmados e estornados do período selecionado.
          </small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide} disabled={loadingPDF || loadingExcel}>
          Cancelar
        </Button>
        <div className="d-flex gap-2">
            <Button 
                variant="outline-danger" 
                onClick={handleGerarPDF}
                disabled={loadingPDF || loadingExcel || !dataInicio || !dataFim}
            >
                {loadingPDF ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Gerando PDF...
                    </>
                ) : (
                    <>
                        <i className="ti ti-file-type-pdf me-2"></i>
                        Baixar PDF
                    </>
                )}
            </Button>
            <Button 
                variant="outline-success" 
                onClick={handleGerarExcel}
                disabled={loadingPDF || loadingExcel || !dataInicio || !dataFim}
            >
                {loadingExcel ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Gerando Excel...
                    </>
                ) : (
                    <>
                        <i className="ti ti-file-spreadsheet me-2"></i>
                        Baixar Excel
                    </>
                )}
            </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default GerarRelatorioModal;
